import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Typography,
  TextField,
  Grid,
  Box,
  InputAdornment,
  Chip,
  Alert,
} from "@mui/material";
import {
  CreditCard,
  Person,
  CalendarToday,
  Lock,
  Shield,
  CheckCircle,
} from "@mui/icons-material";

// ── Config OpenPay ──────────────────────────────────────────
const OPENPAY_MERCHANT_ID = process.env.REACT_APP_OPENPAY_MERCHANT_ID;
const OPENPAY_PUBLIC_KEY = process.env.REACT_APP_OPENPAY_PUBLIC_KEY;
const OPENPAY_SANDBOX = process.env.REACT_APP_OPENPAY_SANDBOX === "true";

// ── Paleta MerryColor ───────────────────────────────────────
const MC = {
  primary: "#801F56",
  primaryLight: "#A93B7A",
  accent: "#E46EA7",
  accentSoft: "#F3CDD4",
  rose: "#D87390",
  blush: "#F8BDD9",
  blushLight: "#FFF0F5",
  white: "#FFFFFF",
  dark: "#2D1B30",
  grey: "#6B6B6B",
  greyLight: "#F5F5F5",
  success: "#4CAF50",
  error: "#E53935",
};

const OpenPayForm = forwardRef(({ onTokenReady }, ref) => {
  const [deviceSessionId, setDeviceSessionId] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [cardType, setCardType] = useState("");

  // ── Inicializar SDK de OpenPay ──────────────────────────
  useEffect(() => {
    try {
      if (!window.OpenPay) {
        setSdkError("El SDK de OpenPay no está cargado. Recarga la página.");
        return;
      }

      if (!OPENPAY_MERCHANT_ID || !OPENPAY_PUBLIC_KEY) {
        setSdkError("Configuración de OpenPay incompleta.");
        return;
      }

      window.OpenPay.setId(OPENPAY_MERCHANT_ID);
      window.OpenPay.setApiKey(OPENPAY_PUBLIC_KEY);
      window.OpenPay.setSandboxMode(OPENPAY_SANDBOX);

      const sessionId = window.OpenPay.deviceData.setup("payment-form");
      setDeviceSessionId(sessionId);
      setSdkReady(true);
    } catch (err) {
      console.error("OpenPay SDK init error:", err);
      setSdkError("Error al inicializar el procesador de pago.");
    }
  }, []);

  // ── Detectar tipo de tarjeta ────────────────────────────
  const detectCardType = useCallback((number) => {
    const clean = number.replace(/\s/g, "");
    if (/^4/.test(clean)) return "visa";
    if (/^5[1-5]/.test(clean)) return "mastercard";
    if (/^3[47]/.test(clean)) return "amex";
    return "";
  }, []);

  // ── Formatear número de tarjeta ─────────────────────────
  const formatCardNumber = (value) => {
    const clean = value.replace(/\D/g, "").slice(0, 16);
    const groups = clean.match(/.{1,4}/g);
    return groups ? groups.join(" ") : clean;
  };

  // ── Formik ──────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      cardName: "",
      cardNumber: "",
      expiryMonth: "",
      expiryYear: "",
      cvv: "",
    },
    validationSchema: Yup.object({
      cardName: Yup.string()
        .min(3, "Mínimo 3 caracteres")
        .required("Nombre del titular requerido"),
      cardNumber: Yup.string()
        .test(
          "valid-card",
          "Número de tarjeta inválido",
          (val) => val && val.replace(/\s/g, "").length >= 15
        )
        .required("Número de tarjeta requerido"),
      expiryMonth: Yup.string()
        .matches(/^(0[1-9]|1[0-2])$/, "Mes inválido (01-12)")
        .required("Mes requerido"),
      expiryYear: Yup.string()
        .matches(/^\d{2}$/, "Año inválido (2 dígitos)")
        .required("Año requerido"),
      cvv: Yup.string()
        .matches(/^\d{3,4}$/, "CVV inválido")
        .required("CVV requerido"),
    }),
    onSubmit: () => { }, // No-op, tokenization is triggered via ref
  });

  // ── Expose tokenize() via ref to parent ─────────────────
  useImperativeHandle(ref, () => ({
    tokenize: () => {
      return new Promise(async (resolve, reject) => {
        // Validate form first
        const errors = await formik.validateForm();
        formik.setTouched({
          cardName: true,
          cardNumber: true,
          expiryMonth: true,
          expiryYear: true,
          cvv: true,
        });

        if (Object.keys(errors).length > 0) {
          reject(new Error("Completa los datos de tu tarjeta correctamente."));
          return;
        }

        if (!sdkReady) {
          reject(new Error("El procesador de pago no está disponible."));
          return;
        }

        setTokenError(null);

        const cardData = {
          card_number: formik.values.cardNumber.replace(/\s/g, ""),
          holder_name: formik.values.cardName,
          expiration_year: formik.values.expiryYear,
          expiration_month: formik.values.expiryMonth,
          cvv2: formik.values.cvv,
        };

        window.OpenPay.token.create(
          cardData,
          (response) => {
            const tokenData = {
              token: response.data.id,
              device_session_id: deviceSessionId,
            };
            if (onTokenReady) onTokenReady(tokenData);
            resolve(tokenData);
          },
          (error) => {
            console.error("OpenPay token error:", error);
            const errorMessages = {
              2004: "El número de tarjeta es inválido.",
              2005: "La fecha de expiración es inválida.",
              2006: "El código de seguridad (CVV) es inválido.",
              2009: "La tarjeta ha expirado.",
              2010: "La tarjeta fue rechazada.",
            };
            const msg =
              errorMessages[error?.data?.error_code] ||
              "No se pudo verificar la tarjeta. Revisa los datos e intenta de nuevo.";
            setTokenError(msg);
            reject(new Error(msg));
          }
        );
      });
    },
    isReady: () => sdkReady,
    hasErrors: () => !formik.isValid,
  }));

  const isLoading = !sdkReady;

  // ── Estilos del campo ───────────────────────────────────
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&.Mui-focused fieldset": { borderColor: MC.primary },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: MC.primary },
  };

  return (
    <Box id="payment-form" sx={{ mt: 2 }}>
      {sdkError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {sdkError}
        </Alert>
      )}

      {tokenError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setTokenError(null)}>
          {tokenError}
        </Alert>
      )}

      <Box
        sx={{
          p: 2.5,
          borderRadius: 3,
          background: `linear-gradient(145deg, ${MC.blushLight} 0%, ${MC.white} 100%)`,
          border: `1px solid ${MC.accentSoft}`,
        }}
      >
        {/* Card type indicator */}
        {cardType && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Chip
              size="small"
              label={cardType.toUpperCase()}
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: 1,
                background: `linear-gradient(135deg, ${MC.primary} 0%, ${MC.accent} 100%)`,
                color: MC.white,
              }}
            />
          </Box>
        )}

        <Grid container spacing={2}>
          {/* Nombre del titular */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="cardName"
              label="Nombre del titular"
              placeholder="Como aparece en la tarjeta"
              value={formik.values.cardName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.cardName && Boolean(formik.errors.cardName)}
              helperText={formik.touched.cardName && formik.errors.cardName}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: MC.accent, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Grid>

          {/* Número de tarjeta */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="cardNumber"
              label="Número de tarjeta"
              placeholder="1234 5678 9012 3456"
              value={formik.values.cardNumber}
              onChange={(e) => {
                const formatted = formatCardNumber(e.target.value);
                formik.setFieldValue("cardNumber", formatted);
                setCardType(detectCardType(formatted));
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.cardNumber && Boolean(formik.errors.cardNumber)}
              helperText={formik.touched.cardNumber && formik.errors.cardNumber}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard sx={{ color: MC.accent, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Grid>

          {/* Mes */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              name="expiryMonth"
              label="Mes"
              placeholder="MM"
              inputProps={{ maxLength: 2 }}
              value={formik.values.expiryMonth}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.expiryMonth && Boolean(formik.errors.expiryMonth)}
              helperText={formik.touched.expiryMonth && formik.errors.expiryMonth}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday sx={{ color: MC.accent, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Grid>

          {/* Año */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              name="expiryYear"
              label="Año"
              placeholder="YY"
              inputProps={{ maxLength: 2 }}
              value={formik.values.expiryYear}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.expiryYear && Boolean(formik.errors.expiryYear)}
              helperText={formik.touched.expiryYear && formik.errors.expiryYear}
              disabled={isLoading}
              sx={fieldSx}
            />
          </Grid>

          {/* CVV */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              name="cvv"
              label="CVV"
              placeholder="123"
              type="password"
              inputProps={{ maxLength: 4 }}
              value={formik.values.cvv}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.cvv && Boolean(formik.errors.cvv)}
              helperText={formik.touched.cvv && formik.errors.cvv}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: MC.accent, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        {/* Security badge */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            py: 1,
            px: 1.5,
            borderRadius: 2,
            backgroundColor: MC.blushLight,
          }}
        >
          <Shield sx={{ color: MC.success, fontSize: 16 }} />
          <Typography variant="caption" sx={{ color: MC.grey, fontWeight: 500 }}>
            Datos protegidos con cifrado de extremo a extremo
          </Typography>
          <CheckCircle sx={{ color: MC.success, fontSize: 14 }} />
        </Box>
      </Box>
    </Box>
  );
});

OpenPayForm.displayName = "OpenPayForm";

export default OpenPayForm;
